import { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  ArrowUpLeft,
  ChevronDown,
  ChevronUp,
  Sparkles,
  ArrowUpDown,
  X,
} from "lucide-react";
import LocalizedLink from "@/components/LocalizedLink";
import PageHero from "@/components/PageHero";
import SaveButton from "@/components/SaveButton";
import ToolLogo from "@/components/ToolLogo";
import { useTools } from "@/content/ContentProvider";
import { pickLang, useLanguage } from "@/i18n/LanguageContext";
import { getToolNameAr } from "@/data/toolNamesAr";
import { trackEvent } from "@/analytics/ga";

/* -----------------------------------------------------------------------
 * Localised UI strings
 * -------------------------------------------------------------------- */
const C = {
  ar: {
    eyebrow: "الأدوات",
    title: "دليل أدوات الذكاء الاصطناعي المجانية",
    lead:
      "أدوات ذكاء اصطناعي مجانية أو ذات خطط مجانية عملية نرشّحها للطلاب والباحثين والمعلمين وصنّاع المحتوى والمبادرات المجتمعية. نظّمنا الأدوات حسب الاستخدام العملي، حتى تصل بسرعة إلى الأداة المناسبة لحاجتك.",
    note:
      "يركّز هذا الدليل على الأدوات التي يمكن البدء بها فورًا دون الحاجة إلى اشتراك مدفوع. قد تتغيّر حدود الخطط المجانية أو بعض المزايا مع الوقت.",
    quickPicksTitle: "ابدأ من هنا",
    quickPicksBody: "اختر نقطة بداية بسيطة بحسب هدفك.",
    filterTitle: "تصفّح حسب القسم",
    filterAll: "الكل",
    filterAria: "أقسام الأدوات (اختيار متعدّد)",
    showMore: "عرض المزيد",
    showLess: "عرض أقل",
    clearFilters: "مسح التصفية",
    free: "مجاني",
    recommended: "الأفضل في هذا القسم",
    bestFor: "مناسب لـ",
    whenToUse: "متى نرشّحه",
    open: "فتح الأداة",
    cta: "جرّب الأداة",
    details: "التفاصيل",
    nameLinkAria: "صفحة تفاصيل الأداة",
    empty: "لا توجد أدوات ضمن هذه التصفية حاليًا.",
    sortTitle: "الترتيب",
    sortAria: "ترتيب الأدوات",
    filteredResultsTitle: "النتائج",
    filteredResultsCount: (n) => `${n} ${n === 1 ? "أداة" : "أدوات"}`,
    sortOptions: {
      default: "الترتيب الافتراضي",
      "a-z": "أبجديًا (أ → ي)",
      "z-a": "أبجديًا (ي → أ)",
      recommended: "الأفضل أولًا",
      recent: "الأحدث أولًا",
    },
  },
  en: {
    eyebrow: "AI Tools",
    title: "Free AI tools directory",
    lead:
      "Free AI tools and practical free-tier platforms recommended for students, researchers, educators, content creators, and community initiatives. Tools are organised by real use case so you can quickly find what fits your need.",
    note:
      "This directory focuses on tools you can start using without a paid subscription. Free-plan limits and features may change over time.",
    quickPicksTitle: "Start here",
    quickPicksBody: "Pick a simple starting point based on your goal.",
    filterTitle: "Browse by category",
    filterAll: "All",
    filterAria: "Tool categories (multi-select)",
    showMore: "Show more",
    showLess: "Show less",
    clearFilters: "Clear filters",
    free: "Free",
    recommended: "Top pick in this category",
    bestFor: "Best for",
    whenToUse: "When we recommend it",
    open: "Open tool",
    cta: "Try Tool",
    details: "Details",
    nameLinkAria: "Open tool details page",
    empty: "No tools match the current filter.",
    sortTitle: "Sort",
    sortAria: "Sort tools",
    filteredResultsTitle: "Results",
    filteredResultsCount: (n) => `${n} tool${n === 1 ? "" : "s"}`,
    sortOptions: {
      default: "Default order",
      "a-z": "A → Z",
      "z-a": "Z → A",
      recommended: "Recommended first",
      recent: "Most recent first",
    },
  },
};

const SORT_VALUES = ["default", "a-z", "z-a", "recommended", "recent"];

/* -----------------------------------------------------------------------
 * Helpers
 * -------------------------------------------------------------------- */
function toolAnchorId(slug) {
  return `tool-${slug}`;
}

/**
 * Apply the active sort to a list of tools without mutating the input.
 * `default` preserves the input order (already pre-sorted by `position` in
 * the API), so it acts as a stable no-op.
 */
function sortTools(tools, sortKey) {
  if (!Array.isArray(tools) || tools.length === 0) return [];
  const arr = [...tools];
  switch (sortKey) {
    case "a-z":
      return arr.sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", "en", { sensitivity: "base" })
      );
    case "z-a":
      return arr.sort((a, b) =>
        (b.name || "").localeCompare(a.name || "", "en", { sensitivity: "base" })
      );
    case "recommended":
      // Recommended tools first, then preserve original order within groups.
      return arr.sort((a, b) => {
        const ra = a.recommended ? 1 : 0;
        const rb = b.recommended ? 1 : 0;
        if (rb !== ra) return rb - ra;
        return (a.position || 0) - (b.position || 0);
      });
    case "recent":
      // Newest by `created_at`. Tools without timestamps fall to the end.
      return arr.sort((a, b) => {
        const da = a.created_at ? new Date(a.created_at).getTime() : 0;
        const db = b.created_at ? new Date(b.created_at).getTime() : 0;
        return db - da;
      });
    case "default":
    default:
      return arr;
  }
}

/* -----------------------------------------------------------------------
 * ToolCard — flat, glassmorphic redesign with link to detail page.
 * -------------------------------------------------------------------- */
function ToolCard({ tool, lang, labels }) {
  const nameAr = getToolNameAr(tool.slug);
  const showAr = !!nameAr && nameAr !== tool.name;

  return (
    <article
      id={toolAnchorId(tool.slug)}
      className="sas-tool-card group relative scroll-mt-28 flex flex-col"
      data-testid={`tool-card-${tool.slug}`}
    >
      <div className="flex items-start justify-between gap-3">
        <ToolLogo tool={tool} size={48} testId={`tool-logo-${tool.slug}`} />
        <div className="flex items-center gap-2 mt-1 shrink-0">
          {tool.recommended && (
            <span
              className="inline-flex items-center gap-1 text-[10.5px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full"
              style={{
                background: "var(--sas-accent-soft)",
                color: "var(--sas-accent)",
              }}
              data-testid={`tool-recommended-${tool.slug}`}
              title={labels.recommended}
            >
              <Sparkles size={11} aria-hidden />
              <span>{labels.recommended}</span>
            </span>
          )}
          <SaveButton
            itemType="tool"
            itemSlug={tool.slug}
            itemId={tool.id}
            variant="card"
            testId={`tool-save-${tool.slug}`}
            stopPropagation={false}
          />
        </div>
      </div>

      <div className="mt-4">
        <h3
          className="text-[17px] font-semibold leading-tight text-[color:var(--sas-ink)]"
          data-testid={`tool-name-en-${tool.slug}`}
          dir="ltr"
        >
          <LocalizedLink
            to={`/tools/${tool.slug}`}
            className="hover:text-[color:var(--sas-accent)] transition-colors focus:outline-none focus-visible:underline"
            aria-label={labels.nameLinkAria}
            data-testid={`tool-detail-link-${tool.slug}`}
          >
            {tool.name}
          </LocalizedLink>
        </h3>
        {showAr && (
          <div
            className="mt-1 text-[13.5px] leading-snug text-[color:var(--sas-ink-2)]"
            data-testid={`tool-name-ar-${tool.slug}`}
            dir="rtl"
            lang="ar"
          >
            {nameAr}
          </div>
        )}
      </div>

      <p
        className="mt-3 text-[13.5px] leading-[1.6] text-[color:var(--sas-ink-2)] line-clamp-2"
        data-testid={`tool-desc-${tool.slug}`}
      >
        {pickLang(tool.desc, lang)}
      </p>

      <div className="flex-1" aria-hidden />

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <a
          href={tool.url}
          target="_blank"
          rel="noreferrer noopener"
          className="sas-tool-cta self-start"
          data-testid={`tool-open-${tool.slug}`}
          onClick={() =>
            trackEvent("tool_open", {
              tool_slug: tool.slug,
              tool_name: tool.name,
              source: "listing_card",
            })
          }
        >
          <span>{labels.cta}</span>
          <ArrowUpLeft size={15} aria-hidden />
        </a>
        <LocalizedLink
          to={`/tools/${tool.slug}`}
          className="text-[12.5px] font-semibold text-[color:var(--sas-accent)] hover:underline"
          data-testid={`tool-details-${tool.slug}`}
        >
          {labels.details} →
        </LocalizedLink>
      </div>
    </article>
  );
}

/* -----------------------------------------------------------------------
 * Grouped section — used when no filter is active and sort is "default".
 * -------------------------------------------------------------------- */
function CategorySection({ category, tools, lang, labels, headingId }) {
  return (
    <section
      className="mt-14 first:mt-0"
      aria-labelledby={headingId}
      data-testid={`tools-section-${category.id}`}
    >
      <header className="mb-6">
        <h2
          id={headingId}
          className="text-[22px] sm:text-[24px] font-semibold text-[color:var(--sas-ink)] leading-tight"
        >
          {pickLang(category.label, lang)}
        </h2>
        {category.blurb && (
          <p className="sas-body mt-2 max-w-3xl text-[14.5px]">
            {pickLang(category.blurb, lang)}
          </p>
        )}
        {category.note && (
          <p
            className="mt-3 text-[12.5px] text-[color:var(--sas-ink-2)] italic"
            data-testid={`tools-section-note-${category.id}`}
          >
            {pickLang(category.note, lang)}
          </p>
        )}
      </header>
      <div className="sas-grid-3">
        {tools.map((tool) => (
          <ToolCard key={tool.slug} tool={tool} lang={lang} labels={labels} />
        ))}
      </div>
    </section>
  );
}

/* -----------------------------------------------------------------------
 * "Start here" — 2 simple text-led starting points.
 * -------------------------------------------------------------------- */
function StartHere({ lang, labels, QUICK_PICKS, getToolBySlug }) {
  const items = QUICK_PICKS.slice(0, 2)
    .map((pick) => {
      const tool = pick.tools.map(getToolBySlug).filter(Boolean)[0] || null;
      return tool ? { ...pick, tool } : null;
    })
    .filter(Boolean);

  if (items.length === 0) return null;

  return (
    <section
      className="rounded-2xl border border-[color:var(--sas-line)] bg-[color:var(--sas-accent-soft)]/25 px-6 py-6 sm:px-8 sm:py-7 mb-12"
      aria-labelledby="start-here-title"
      data-testid="tools-start-here"
    >
      <div className="sas-eyebrow mb-1">{labels.quickPicksTitle}</div>
      <h2
        id="start-here-title"
        className="text-[17px] sm:text-[18px] font-semibold text-[color:var(--sas-ink)]"
      >
        {labels.quickPicksBody}
      </h2>

      <ul
        className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-5"
        data-testid="tools-start-here-list"
      >
        {items.map(({ id, need, tool }) => (
          <li
            key={id}
            className="flex flex-col gap-1"
            data-testid={`tools-quick-pick-${id}`}
          >
            <span className="text-[13px] text-[color:var(--sas-ink-2)] leading-snug">
              {pickLang(need, lang)}
            </span>
            <LocalizedLink
              to={`/tools/${tool.slug}`}
              className="text-[15px] font-semibold text-[color:var(--sas-accent)] hover:underline leading-snug"
              data-testid={`tools-quick-pick-${id}-${tool.slug}`}
            >
              {tool.name}
            </LocalizedLink>
          </li>
        ))}
      </ul>
    </section>
  );
}

/* -----------------------------------------------------------------------
 * Multi-select pill (toggleable). Replaces the previous tab-style filter.
 * -------------------------------------------------------------------- */
function FilterPill({ pill, isActive, isAllPill, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      // Multi-select semantics: each pill is a toggle, not a tab. We use
      // `aria-pressed` for assistive tech. The synthetic "All" pill keeps
      // tab-like behaviour (selected only when nothing else is selected).
      aria-pressed={isActive}
      data-testid={`filter-${pill.id}`}
      className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[13px] font-medium border transition-colors ${
        isActive
          ? "bg-[color:var(--sas-accent)] text-white border-[color:var(--sas-accent)]"
          : "bg-white text-[color:var(--sas-ink)] border-[color:var(--sas-line)] hover:border-[color:var(--sas-ink)]"
      }`}
    >
      <span>{pill.label}</span>
      {pill.count != null && !isAllPill && (
        <span
          className={`text-[11px] font-semibold rounded-full px-1.5 py-0.5 ${
            isActive
              ? "bg-white/20 text-white"
              : "bg-[color:var(--sas-accent-soft)] text-[color:var(--sas-accent)]"
          }`}
          aria-hidden
        >
          {pill.count}
        </span>
      )}
      {isAllPill && pill.count != null && (
        <span
          className={`text-[11px] font-semibold rounded-full px-1.5 py-0.5 ${
            isActive
              ? "bg-white/20 text-white"
              : "bg-[color:var(--sas-accent-soft)] text-[color:var(--sas-accent)]"
          }`}
          aria-hidden
        >
          {pill.count}
        </span>
      )}
    </button>
  );
}

/* -----------------------------------------------------------------------
 * Page
 * -------------------------------------------------------------------- */
export default function AITools() {
  const { lang } = useLanguage();
  const labels = C[lang];
  const [searchParams, setSearchParams] = useSearchParams();
  const [showAllCats, setShowAllCats] = useState(false);
  const {
    TOOLS,
    TOOL_CATEGORIES,
    QUICK_PICKS,
    getToolBySlug,
    getToolsByCategory,
    primaryCategoryOf,
  } = useTools();

  // -------------------- URL state: cats + sort --------------------
  // `cats=a,b,c` and `sort=a-z`. Read once into state so subsequent
  // toggles update both the local state AND the URL.
  const [activeCats, setActiveCats] = useState(() => {
    const raw = searchParams.get("cats") || "";
    const parts = raw
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    return new Set(parts);
  });
  const [sort, setSort] = useState(() => {
    const raw = (searchParams.get("sort") || "default").trim();
    return SORT_VALUES.includes(raw) ? raw : "default";
  });

  // Validate active cats against current category list (drops unknown ids).
  useEffect(() => {
    if (TOOL_CATEGORIES.length === 0) return;
    const valid = new Set(TOOL_CATEGORIES.map((c) => c.id));
    let changed = false;
    const next = new Set();
    activeCats.forEach((id) => {
      if (valid.has(id)) {
        next.add(id);
      } else {
        changed = true;
      }
    });
    if (changed) setActiveCats(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [TOOL_CATEGORIES]);

  // Sync state -> URL params (cheap, no scroll).
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    if (activeCats.size === 0) {
      params.delete("cats");
    } else {
      params.set("cats", Array.from(activeCats).join(","));
    }
    if (sort === "default") {
      params.delete("sort");
    } else {
      params.set("sort", sort);
    }
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCats, sort]);

  // -------------------- Filter handlers --------------------
  const toggleCat = useCallback((id) => {
    setActiveCats((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  const clearAllCats = useCallback(() => setActiveCats(new Set()), []);

  // -------------------- Derived view data --------------------
  // Pills configuration. The "All" synthetic pill is only "selected" when no
  // categories are active.
  const PRIMARY_COUNT = 3;
  const primaryCategoryEntries = TOOL_CATEGORIES.slice(0, PRIMARY_COUNT);
  const extraCategoryEntries = TOOL_CATEGORIES.slice(PRIMARY_COUNT);

  // Auto-expand the "extra" pills row whenever any of those extras is
  // currently selected, otherwise the user can't see what's active.
  const extraIds = new Set(extraCategoryEntries.map((c) => c.id));
  const hasActiveExtras = Array.from(activeCats).some((id) => extraIds.has(id));
  const isExpanded = showAllCats || hasActiveExtras;

  // Decide between grouped layout and flat layout.
  //   - grouped: no cat filter AND default sort
  //   - flat:    any filter or any non-default sort
  const useFlatLayout = activeCats.size > 0 || sort !== "default";

  const flatTools = useMemo(() => {
    if (!useFlatLayout) return [];
    let pool;
    if (activeCats.size === 0) {
      pool = TOOLS;
    } else {
      const ids = Array.from(activeCats);
      // OR-style filter: a tool that matches ANY selected category is in.
      pool = TOOLS.filter(
        (t) =>
          Array.isArray(t.categories) &&
          t.categories.some((c) => ids.includes(c))
      );
    }
    return sortTools(pool, sort);
  }, [useFlatLayout, activeCats, TOOLS, sort]);

  // Grouped layout — only used when nothing is filtered/sorted.
  const groupedSections = useMemo(() => {
    if (useFlatLayout) return [];
    return TOOL_CATEGORIES;
  }, [useFlatLayout, TOOL_CATEGORIES]);

  return (
    <>
      <PageHero eyebrow={labels.eyebrow} title={labels.title} lead={labels.lead} />

      <section className="sas-section" data-testid="tools-page">
        <div className="sas-container">
          <p className="sas-body text-[14px] max-w-3xl mb-10 text-[color:var(--sas-ink-2)]">
            {labels.note}
          </p>

          <StartHere
            lang={lang}
            labels={labels}
            QUICK_PICKS={QUICK_PICKS}
            getToolBySlug={getToolBySlug}
          />

          {/* Filter + sort row */}
          <div className="mb-10 flex flex-col gap-4">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="sas-eyebrow mb-3">{labels.filterTitle}</div>
                <div
                  className="flex flex-wrap gap-2"
                  role="group"
                  aria-label={labels.filterAria}
                  data-testid="tools-filter"
                >
                  {/* "All" synthetic pill — clears active cats */}
                  <FilterPill
                    pill={{ id: "all", label: labels.filterAll, count: TOOLS.length }}
                    isActive={activeCats.size === 0}
                    isAllPill
                    onClick={clearAllCats}
                  />
                  {primaryCategoryEntries.map((c) => (
                    <FilterPill
                      key={c.id}
                      pill={{
                        id: c.id,
                        label: pickLang(c.label, lang),
                        count: getToolsByCategory(c.id).length,
                      }}
                      isActive={activeCats.has(c.id)}
                      onClick={() => toggleCat(c.id)}
                    />
                  ))}

                  {!isExpanded && extraCategoryEntries.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setShowAllCats(true)}
                      className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[13px] font-medium border border-dashed border-[color:var(--sas-line)] text-[color:var(--sas-ink-2)] hover:text-[color:var(--sas-ink)] hover:border-[color:var(--sas-ink)] transition-colors"
                      data-testid="tools-filter-show-more"
                      aria-expanded="false"
                      aria-controls="tools-filter-extras"
                    >
                      <ChevronDown size={14} aria-hidden />
                      {labels.showMore}
                    </button>
                  )}

                  {isExpanded && (
                    <div
                      id="tools-filter-extras"
                      className="flex flex-wrap gap-2 basis-full"
                    >
                      {extraCategoryEntries.map((c) => (
                        <FilterPill
                          key={c.id}
                          pill={{
                            id: c.id,
                            label: pickLang(c.label, lang),
                            count: getToolsByCategory(c.id).length,
                          }}
                          isActive={activeCats.has(c.id)}
                          onClick={() => toggleCat(c.id)}
                        />
                      ))}
                      <button
                        type="button"
                        onClick={() => setShowAllCats(false)}
                        className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[13px] font-medium text-[color:var(--sas-ink-2)] hover:text-[color:var(--sas-ink)] transition-colors"
                        data-testid="tools-filter-show-less"
                        aria-expanded="true"
                        aria-controls="tools-filter-extras"
                      >
                        <ChevronUp size={14} aria-hidden />
                        {labels.showLess}
                      </button>
                    </div>
                  )}

                  {activeCats.size > 0 && (
                    <button
                      type="button"
                      onClick={clearAllCats}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12.5px] font-medium text-[color:var(--sas-ink-2)] hover:text-[color:var(--sas-ink)] transition-colors"
                      data-testid="tools-filter-clear"
                    >
                      <X size={13} aria-hidden />
                      {labels.clearFilters}
                    </button>
                  )}
                </div>
              </div>

              {/* Sort dropdown */}
              <div className="shrink-0 flex flex-col gap-1.5">
                <label
                  className="sas-eyebrow"
                  htmlFor="tools-sort"
                  data-testid="tools-sort-label"
                >
                  {labels.sortTitle}
                </label>
                <div className="relative">
                  <ArrowUpDown
                    size={14}
                    className="absolute top-1/2 -translate-y-1/2 start-3 text-[color:var(--sas-ink-2)] pointer-events-none"
                    aria-hidden
                  />
                  <select
                    id="tools-sort"
                    value={sort}
                    onChange={(e) => setSort(e.target.value)}
                    aria-label={labels.sortAria}
                    data-testid="tools-sort"
                    className="ps-9 pe-8 py-2 rounded-full text-[13px] font-medium border border-[color:var(--sas-line)] bg-white text-[color:var(--sas-ink)] hover:border-[color:var(--sas-ink)] transition-colors appearance-none cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--sas-accent)]/40"
                  >
                    {SORT_VALUES.map((v) => (
                      <option key={v} value={v} data-testid={`tools-sort-option-${v}`}>
                        {labels.sortOptions[v]}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={14}
                    className="absolute top-1/2 -translate-y-1/2 end-3 text-[color:var(--sas-ink-2)] pointer-events-none"
                    aria-hidden
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Results body — grouped vs. flat */}
          {useFlatLayout ? (
            flatTools.length === 0 ? (
              <div
                className="text-center py-16 text-[color:var(--sas-ink-2)]"
                data-testid="tools-empty"
              >
                {labels.empty}
              </div>
            ) : (
              <div data-testid="tools-flat">
                <header className="mb-6">
                  <h2 className="text-[22px] sm:text-[24px] font-semibold text-[color:var(--sas-ink)] leading-tight">
                    {labels.filteredResultsTitle}
                  </h2>
                  <p
                    className="sas-body mt-2 max-w-3xl text-[14.5px]"
                    data-testid="tools-flat-count"
                  >
                    {labels.filteredResultsCount(flatTools.length)}
                  </p>
                </header>
                <div className="sas-grid-3">
                  {flatTools.map((tool) => (
                    <ToolCard
                      key={tool.slug}
                      tool={tool}
                      lang={lang}
                      labels={labels}
                    />
                  ))}
                </div>
              </div>
            )
          ) : (
            <div data-testid="tools-sections">
              {groupedSections.map((category) => {
                const tools = getToolsByCategory(category.id);
                if (tools.length === 0) return null;
                return (
                  <CategorySection
                    key={category.id}
                    category={category}
                    tools={tools}
                    lang={lang}
                    labels={labels}
                    headingId={`cat-${category.id}`}
                  />
                );
              })}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
